import { useCallback, useEffect, useState } from 'react';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import type { PlanJoinRequest, TonyteActivity } from '../types';
import { assertSocialWriteAllowed } from '../utils/verificationGate';

const isStudiZonePlan = (plan: TonyteActivity) =>
  plan.layer === 'studizone' || plan.isStudentOnly === true || (plan as any).source === 'studizone';

const isStudiZoneRequestData = (data: any) =>
  data?.layer === 'studizone' || data?.isStudentOnly === true;

// ─── User's pending plan request IDs (all plans) ─────────────────────────────

export function useUserPendingPlanRequests(userId: string | null): Set<string> {
  const [pendingPlanIds, setPendingPlanIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!userId) { setPendingPlanIds(new Set()); return; }
    const q = query(
      collection(firestore, FIRESTORE_COLLECTIONS.planJoinRequests),
      where('fromUserId', '==', userId),
      where('status', '==', 'pending'),
    );
    return onSnapshot(q, snap => {
      setPendingPlanIds(new Set(snap.docs.filter(d => !isStudiZoneRequestData(d.data())).map(d => d.data().planId as string)));
    }, () => setPendingPlanIds(new Set()));
  }, [userId]);

  return pendingPlanIds;
}

// ─── Admin: all pending requests across all admin's plans ─────────────────────

export function useAdminAllPendingRequests(userId: string | null): { requests: PlanJoinRequest[]; loading: boolean } {
  const [requests, setRequests] = useState<PlanJoinRequest[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));

  useEffect(() => {
    if (!userId) { setRequests([]); setLoading(false); return; }
    const q = query(
      collection(firestore, FIRESTORE_COLLECTIONS.planJoinRequests),
      where('adminIds', 'array-contains', userId),
      where('status', '==', 'pending'),
    );
    return onSnapshot(q, snap => {
      setRequests(snap.docs.map(d => {
        const data = d.data();
        if (isStudiZoneRequestData(data)) return null;
        return {
          id: d.id,
          planId: data.planId,
          planTitle: data.planTitle,
          fromUserId: data.fromUserId,
          adminIds: Array.isArray(data.adminIds) ? data.adminIds : [],
          message: data.message,
          status: data.status,
          createdAt: data.createdAt ?? '',
          updatedAt: data.updatedAt ?? '',
        };
      }).filter(Boolean) as PlanJoinRequest[]);
      setLoading(false);
    }, () => { setRequests([]); setLoading(false); });
  }, [userId]);

  return { requests, loading };
}

export function useStudiZonePendingRequests(userId: string | null): { requests: PlanJoinRequest[]; loading: boolean } {
  const [requests, setRequests] = useState<PlanJoinRequest[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));

  useEffect(() => {
    if (!userId) { setRequests([]); setLoading(false); return; }
    const q = query(
      collection(firestore, FIRESTORE_COLLECTIONS.planJoinRequests),
      where('layer', '==', 'studizone'),
      where('adminId', '==', userId),
      where('status', '==', 'pending'),
    );
    return onSnapshot(q, snap => {
      setRequests(snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          planId: data.planId,
          planTitle: data.planTitle,
          fromUserId: data.fromUserId || data.requesterId,
          requesterId: data.requesterId || data.fromUserId,
          adminIds: Array.isArray(data.adminIds) ? data.adminIds : [data.adminId].filter(Boolean),
          adminId: data.adminId,
          layer: data.layer,
          isStudentOnly: data.isStudentOnly,
          message: data.message,
          status: data.status,
          createdAt: data.createdAt ?? '',
          updatedAt: data.updatedAt ?? '',
        };
      }));
      setLoading(false);
    }, () => { setRequests([]); setLoading(false); });
  }, [userId]);

  return { requests, loading };
}

// ─── User's own request for a specific plan ───────────────────────────────────

export function usePlanJoinRequest(planId: string | null, userId: string | null) {
  const [request, setRequest] = useState<PlanJoinRequest | null | 'loading'>('loading');

  useEffect(() => {
    if (!planId || !userId) {
      setRequest(null);
      return;
    }
    const q = query(
      collection(firestore, FIRESTORE_COLLECTIONS.planJoinRequests),
      where('planId', '==', planId),
      where('fromUserId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    return onSnapshot(
      q,
      snap => {
        if (snap.empty) {
          setRequest(null);
          return;
        }
        const d = snap.docs[0];
        const data = d.data();
        setRequest({
          id: d.id,
          planId: data.planId,
          planTitle: data.planTitle,
          fromUserId: data.fromUserId,
          requesterId: data.requesterId || data.fromUserId,
          adminIds: Array.isArray(data.adminIds) ? data.adminIds : [],
          adminId: data.adminId,
          layer: data.layer,
          isStudentOnly: data.isStudentOnly,
          message: data.message,
          status: data.status,
          createdAt: data.createdAt ?? '',
          updatedAt: data.updatedAt ?? '',
          reviewedBy: data.reviewedBy,
          reviewedAt: data.reviewedAt,
        });
      },
      () => setRequest(null),
    );
  }, [planId, userId]);

  const sendRequest = useCallback(async (plan: TonyteActivity, message?: string) => {
    assertSocialWriteAllowed();
    if (!userId || !plan.id) throw new Error('missing userId or planId');
    const adminIds =
      Array.isArray(plan.adminIds) && plan.adminIds.length > 0
        ? plan.adminIds
        : ([plan.hostId, plan.createdBy].filter(Boolean) as string[]);
    const studiZone = isStudiZonePlan(plan);
    await addDoc(collection(firestore, FIRESTORE_COLLECTIONS.planJoinRequests), {
      planId: plan.id,
      planTitle: plan.title || '',
      fromUserId: userId,
      requesterId: userId,
      adminIds,
      adminId: adminIds[0] || '',
      message: message?.trim() || '',
      status: 'pending',
      ...(studiZone ? { layer: 'studizone', isStudentOnly: true } : {}),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }, [userId]);

  const cancelRequest = useCallback(async (requestId: string) => {
    await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.planJoinRequests, requestId), {
      status: 'cancelled',
      updatedAt: new Date().toISOString(),
    });
  }, []);

  return {
    request: request === 'loading' ? undefined : request,
    loading: request === 'loading',
    sendRequest,
    cancelRequest,
  };
}

// ─── Admin: pending requests for a plan ──────────────────────────────────────

export function usePlanJoinRequests(planId: string | null, isAdmin: boolean, adminUserId?: string | null) {
  const [requests, setRequests] = useState<PlanJoinRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!planId || !isAdmin || !adminUserId) {
      setRequests([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(firestore, FIRESTORE_COLLECTIONS.planJoinRequests),
      where('planId', '==', planId),
      where('adminIds', 'array-contains', adminUserId),
      where('status', '==', 'pending'),
    );
    return onSnapshot(
      q,
      snap => {
        setRequests(
          snap.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              planId: data.planId,
              planTitle: data.planTitle,
              fromUserId: data.fromUserId,
              adminIds: Array.isArray(data.adminIds) ? data.adminIds : [],
              message: data.message,
              status: data.status,
              createdAt: data.createdAt ?? '',
              updatedAt: data.updatedAt ?? '',
            };
          }).filter(Boolean) as PlanJoinRequest[],
        );
        setLoading(false);
      },
      () => { setRequests([]); setLoading(false); },
    );
  }, [adminUserId, isAdmin, planId]);

  const acceptRequest = useCallback(async (
    req: PlanJoinRequest,
    plan: TonyteActivity,
    adminUserId: string,
  ) => {
    assertSocialWriteAllowed();
    const userSnap = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, req.fromUserId));
    const userData = userSnap.exists() ? userSnap.data() : {};
    const displayName = String(userData.displayName || userData.name || 'Traveler');
    const photoURL = String(userData.profileImage || userData.profilePhotoURL || userData.photoURL || '');

    const batch = writeBatch(firestore);
    batch.update(doc(firestore, FIRESTORE_COLLECTIONS.planJoinRequests, req.id), {
      status: isStudiZonePlan(plan) ? 'approved' : 'accepted',
      reviewedBy: adminUserId,
      reviewedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    batch.update(doc(firestore, FIRESTORE_COLLECTIONS.plans, plan.id), {
      participantIds: arrayUnion(req.fromUserId),
      members: arrayUnion(req.fromUserId),
      approvedRequests: arrayUnion(req.fromUserId),
      attendees: arrayUnion({ id: req.fromUserId, name: displayName, photo: photoURL }),
      participants: increment(1),
      // Clears a prior admin removal so a deliberately re-accepted user
      // isn't still filtered out of membership by planMembership.ts (which
      // subtracts removedUserIds) or blocked from Plan re-request UI by
      // EventDetailScreen's isRemovedFromPlan check.
      removedUserIds: arrayRemove(req.fromUserId),
    });
    batch.set(
      doc(firestore, FIRESTORE_COLLECTIONS.plans, plan.id, 'participants', req.fromUserId),
      { userId: req.fromUserId, displayName, photoURL, joinedAt: serverTimestamp() },
    );
    batch.update(doc(firestore, FIRESTORE_COLLECTIONS.planChats, plan.id), {
      memberIds: arrayUnion(req.fromUserId),
      members: arrayUnion(req.fromUserId),
      // Clears a prior admin removal's ban so the messages create rule
      // (which checks bannedUserIds) doesn't keep silently rejecting sends
      // from a user who was just deliberately re-admitted.
      bannedUserIds: arrayRemove(req.fromUserId),
      ...(isStudiZonePlan(plan) ? { layer: 'studizone', chatType: 'studizone_plan', isStudentOnly: true } : {}),
    });
    await batch.commit();
  }, []);

  const declineRequest = useCallback(async (requestId: string, adminUserId: string) => {
    const requestSnap = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.planJoinRequests, requestId));
    const requestData = requestSnap.exists() ? requestSnap.data() : {};
    await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.planJoinRequests, requestId), {
      status: isStudiZoneRequestData(requestData) ? 'rejected' : 'declined',
      reviewedBy: adminUserId,
      reviewedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  }, []);

  const removeParticipant = useCallback(async (
    participantUserId: string,
    plan: TonyteActivity,
  ) => {
    const existingAttendee = plan.attendees?.find(
      a => a.id === participantUserId || (a as any).userId === participantUserId,
    );
    await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, plan.id), {
      participantIds: arrayRemove(participantUserId),
      members: arrayRemove(participantUserId),
      removedUserIds: arrayUnion(participantUserId),
      ...(existingAttendee ? { attendees: arrayRemove(existingAttendee) } : {}),
      participants: increment(-1),
    });
    await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.planChats, plan.id), {
      memberIds: arrayRemove(participantUserId),
      bannedUserIds: arrayUnion(participantUserId),
    });
  }, []);

  return { requests, loading, acceptRequest, declineRequest, removeParticipant };
}
